/**
 * A Nassau that is running right now.
 *
 * `nassau.ts` walks two finished cards at the clubhouse. This makes the same
 * match live: struck before anyone tees off, standing as the scores arrive,
 * several of them going at once. It stores no press decisions at all — the two
 * modes it supports are the two that do not need any — so a match is a row of
 * terms and everything else is still derived from the cards, exactly as the
 * engine's header insists.
 *
 * The two hard-won facts in here:
 *
 *   - `auto` is a FIXPOINT, not a pass. Accepting a press appends a bet, that
 *     bet is live on later holes, and it raises offers of its own that only
 *     exist on a subsequent run. See `autoDecisions`.
 *   - `amountCents` is cents and `NassauInput.amountPerBet` is dollars. The
 *     conversion happens once, in `amountPerBet`, because doing it anywhere else
 *     renders every figure in the app a hundred times too large.
 */

import { courseHandicapFor, findTee, holesFor, playingIndex } from './handicap'
import { runNassau, type NassauInput, type NassauWalk, type PressDecision } from './nassau'
import type { Group, Hole, HoleScore, Round, Tournament } from './types'

/**
 * How presses are handled, chosen at setup and frozen thereafter.
 *
 * `offered` is in the type and in the schema's CHECK because it is the mode the
 * group actually plays, but it needs stored decisions and a way to answer a
 * question from a phone, so it is not built yet and is refused server-side.
 */
export type PressMode = 'none' | 'auto' | 'offered'

/** A match as the app holds it: the terms, and nothing derived. */
export interface SideMatch {
  id: string
  /**
   * The eighteen this match covers. Order is not authoritative — `sliceFor`
   * sorts by `round.seq`, so a list written back to front still plays forward.
   */
  roundIds: string[]
  playerA: string
  playerB: string
  /** Strokes A gives B. Negative means B gives A. Frozen at setup. */
  strokes: number
  /** CENTS. Every bet in the match is worth this, presses included. */
  amountCents: number
  pressMode: PressMode
  createdBy: string
  createdAt: string
  /** Either man may void. The row stays so the other can see that it was. */
  voidedAt: string | null
  voidedBy: string | null
}

/** Cents in the table, dollars in the engine. Convert here or nowhere. */
export const amountPerBet = (amountCents: number): number => amountCents / 100

/**
 * A guard, not a rule.
 *
 * Nothing about the group's rules caps how deep presses go, and `auto` on a
 * one-sided eighteen legitimately reaches depth six. This exists only so that a
 * bug in the loop below — or an engine change that makes an offer permanent —
 * cannot spin forever on a phone. Nothing real gets near it: the worst case the
 * spec measured, twelve holes lost in a row, converges in seven.
 */
export const AUTO_PRESS_CAP = 40

/**
 * Every press the engine would have offered, taken.
 *
 * ITERATED TO A FIXPOINT, and that is the whole of this function. Accepting a
 * press appends a bet (nassau.ts:330-341); that bet then runs on every later
 * hole and goes two down itself, which raises an offer that did not exist on the
 * run that created it. One pass over `steps[].offers` therefore underpays badly
 * — on a $20 Nassau where one man wins holes 1 to 12 it yields 6 bets and $120,
 * where the truth is 13 bets and $240 — and one pass over `pending` is worse
 * still, because `pending` is already collapsed to the earliest offer only
 * (nassau.ts:409-410).
 *
 * So the loop runs the walk again with what it has agreed so far, accepts
 * whatever is outstanding, and goes round until nothing is. `pending` being
 * collapsed is why it takes seven iterations rather than two; it is not a
 * problem to route around, since the answer is the same either way and the walk
 * is cheap.
 */
export function autoDecisions(base: Omit<NassauInput, 'decisions'>): PressDecision[] {
  const decisions: PressDecision[] = []
  for (let pass = 0; pass < AUTO_PRESS_CAP; pass += 1) {
    const walk = runNassau({ ...base, decisions })
    if (walk.pending.length === 0) return decisions
    for (const offer of walk.pending) {
      decisions.push({ parentId: offer.parentId, atIndex: offer.atIndex, accepted: true })
    }
  }
  return decisions
}

export interface SideMatchInput {
  mode: PressMode
  holes: Hole[]
  grossA: (number | null)[]
  grossB: (number | null)[]
  strokes: number
  amountCents: number
  nameA: string
  nameB: string
}

/**
 * Where a live match stands.
 *
 * `none` ignores the engine's offers entirely rather than suppressing them: with
 * no decisions supplied, an unanswered offer sits in `pending` forever and no
 * bet is ever created, which is precisely front, back and total and nothing
 * else. The offers still appear in `steps[].offers`, and no caller of this
 * function may read them for a `none` match.
 */
export function runSideMatch(input: SideMatchInput): NassauWalk {
  const base: Omit<NassauInput, 'decisions'> = {
    holes: input.holes,
    grossA: input.grossA,
    grossB: input.grossB,
    strokes: input.strokes,
    amountPerBet: amountPerBet(input.amountCents),
    nameA: input.nameA,
    nameB: input.nameB,
  }
  return runNassau({
    ...base,
    decisions: input.mode === 'auto' ? autoDecisions(base) : [],
  })
}

// ---------------------------------------------------------------------------
// which holes a match covers
// ---------------------------------------------------------------------------

export interface Slice {
  holes: Hole[]
  /** Which round each position came from, so a repeated hole number is clear. */
  roundLabels: string[]
  grossFor: (playerId: string) => (number | null)[]
  /**
   * Rounds the bet was struck over that are no longer in the document.
   *
   * A struck bet names its rounds and the holes are re-read on every render, so
   * deleting one silently shortened the card underneath it: an eighteen became
   * a nine, and front, back and total collapsed into one bet that settled for
   * a quarter of the money. Nothing said so — the row simply re-labeled itself
   * "so far", which reads like an ordinary bet in progress.
   */
  missingRoundIds: string[]
}

/**
 * Assemble one continuous card out of the rounds chosen, in playing order.
 *
 * Sorted by `round.seq` and NOT by the order of `roundIds`. That is deliberate
 * and load-bearing in two places at once: the post-round walkthrough and the
 * live board both derive from this, and if the two disagreed about which nine
 * came first they would disagree about the front, the back and the money.
 *
 * Lives here rather than in a screen because both surfaces need it, and the
 * server needs the hole count it produces to decide whether a match is legal at
 * all.
 */
export function sliceFor(
  doc: Pick<Tournament, 'rounds' | 'courses'>,
  roundIds: string[],
  scores: HoleScore[],
): Slice {
  const rounds = byPlay(doc.rounds.filter((r) => roundIds.includes(r.id)))
  const missingRoundIds = roundIds.filter((id) => !doc.rounds.some((r) => r.id === id))

  const holes: Hole[] = []
  const keys: { roundId: string; holeNo: number }[] = []
  const roundLabels: string[] = []
  for (const r of rounds) {
    const course = doc.courses.find((c) => c.id === r.courseId)
    if (!course) continue
    for (const h of holesFor(course, r.nine)) {
      holes.push(h)
      keys.push({ roundId: r.id, holeNo: h.number })
      roundLabels.push(r.label)
    }
  }

  return {
    holes,
    roundLabels,
    missingRoundIds,
    grossFor: (playerId) =>
      keys.map((k, i) => {
        const s = scores.find(
          (x) => x.roundId === k.roundId && x.playerId === playerId && x.holeNo === k.holeNo,
        )
        if (!s) return null
        if (s.value.state === 'holed') return s.value.gross
        /*
         * A pickup counts, it does not stall. The kept number if one was
         * kept, otherwise a triple — Scott's ruling, replacing the state
         * where a bare pickup read as "not scored" forever and the bets
         * spanning it could never close: his own Nassau sat at "2 still
         * open" with both cards signed, over one 15th nobody finished.
         */
        if (s.value.state === 'pickedUp') {
          return s.value.mostLikely ?? (holes[i]?.par ?? 4) + 3
        }
        // A conceded cell stalled a bet the same way a bare pickup did —
        // the review agent's observation. Same ruling: it counts a triple.
        if (s.value.state === 'conceded') return (holes[i]?.par ?? 4) + 3
        return null
      }),
  }
}

/**
 * The rounds in the order they are played — the day, then the tee time.
 *
 * Local to this module and deliberately not `inPlayOrder`: that one renumbers
 * `seq` as it sorts, which is a thing to do to a document being edited and not
 * to a bet being priced.
 */
const byPlay = <R extends { date?: string; firstTeeTime?: string; seq: number }>(
  rounds: readonly R[],
): R[] =>
  [...rounds].sort((a, b) => {
    const key = (x: R) => `${x.date ?? ''} ${x.firstTeeTime ?? ''}`
    return key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : a.seq - b.seq
  })

/**
 * A Nassau is front, back and total, so it is exactly eighteen holes.
 *
 * Null for anything else, and the server refuses on the same test rather than
 * trusting the UI to only offer legal sets. An orphan nine cannot host one, and
 * a 27-hole list is worse than useless: `baseBets` takes `half = 13` and hands
 * back a front and a back that nobody agreed to play.
 */
export function eighteenFor(
  doc: Pick<Tournament, 'rounds' | 'courses'>,
  roundIds: string[],
): { rounds: Round[]; holes: Hole[] } | null {
  // Every id has to resolve. A list holding one round twice, or one that an
  // admin has since deleted, would otherwise pass on a hole count made up of
  // the rounds that did resolve.
  const rounds = byPlay(doc.rounds.filter((r) => roundIds.includes(r.id)))
  if (rounds.length !== new Set(roundIds).size) return null

  const holes = sliceFor(doc, roundIds, []).holes
  if (holes.length !== 18) return null
  return { rounds, holes }
}

export interface Eighteen {
  roundIds: string[]
  label: string
}

/**
 * Every eighteen this week offers: one 18-hole round, or two nines that make one.
 *
 * Two nines only when they are consecutive on the same course and the same day
 * — which is what the trip actually plays, and is the only pairing where "the
 * front" and "the back" mean what everyone at the table thinks they mean.
 *
 * Consecutive by the CLOCK, not by `seq`. A round added from a phone takes the
 * next number after the championship, so ordering the week by `seq` both hides
 * the eighteen it completes and makes it "adjacent" to whatever sorts last —
 * offering an eighteen nobody played. The order also decides which nine is the
 * front, and the front is half the bet.
 */
export function eighteens(doc: Pick<Tournament, 'rounds' | 'courses'>): Eighteen[] {
  const ordered = byPlay(doc.rounds)
  const out: Eighteen[] = []
  for (let i = 0; i < ordered.length; i += 1) {
    const r = ordered[i]!
    if (eighteenFor(doc, [r.id])) {
      out.push({ roundIds: [r.id], label: r.label })
      continue
    }
    const next = ordered[i + 1]
    if (!next) continue
    if (next.courseId !== r.courseId || next.date !== r.date) continue
    /*
     * Two different nines. Front + front is eighteen hole ENTRIES and nine
     * holes of golf — the same nine counted twice — and the length check below
     * cannot tell the difference. Nobody walked that eighteen.
     */
    if (next.nine === r.nine) continue
    if (eighteenFor(doc, [r.id, next.id])) {
      out.push({ roundIds: [r.id, next.id], label: `${r.label} + ${next.label}` })
    }
  }
  return out
}

/**
 * The strokes the indexes say, which is where a negotiation starts.
 *
 * Course handicap per round — `HI × Slope/113 + (CR − Par)`, off each player's
 * own tee — summed across the rounds chosen, so two nines add up to the eighteen
 * they were played as. The difference is what the better player gives, which is
 * standard match play: the low man plays off scratch.
 *
 * Summed per round rather than taken as one eighteen-hole figure because the
 * rating differential is per course, and that last term is what levels two men
 * playing different tees.
 *
 * A live match does NOT re-read this. It is an opening offer at setup and then
 * the number is the deal — see `SideMatch.strokes`.
 */
export function autoStrokesFor(
  doc: Pick<Tournament, 'players' | 'rounds' | 'courses'>,
  playerA: string,
  playerB: string,
  roundIds: string[],
): number {
  const pa = doc.players.find((p) => p.id === playerA)
  const pb = doc.players.find((p) => p.id === playerB)
  if (!pa || !pb) return 0
  let chA = 0
  let chB = 0
  for (const r of doc.rounds.filter((x) => roundIds.includes(x.id))) {
    const course = doc.courses.find((c) => c.id === r.courseId)
    if (!course) continue
    const holes = holesFor(course, r.nine)
    chA += courseHandicapFor(playingIndex(pa), findTee(course, pa), holes)
    chB += courseHandicapFor(playingIndex(pb), findTee(course, pb), holes)
  }
  /*
   * chB − chA, and the direction is the whole function. The convention on
   * `SideMatch.strokes` is "positive means A GIVES B", and the man who gives is
   * the LOWER handicap — so when B carries the bigger number, A gives, and the
   * result must be positive. This returned chA − chB for its first two days,
   * which inverted every "from the handicaps" deal in both the copy and the
   * money: the 3 handicap RECEIVED ten strokes from the 12 and collected on a
   * level card. Caught by the pre-trip audit; the sign is now pinned by tests
   * on real roster numbers, because no compiler can see it.
   */
  return chB - chA
}

// ---------------------------------------------------------------------------
// settlement
// ---------------------------------------------------------------------------

/**
 * The cards that still have to be signed before this match is money.
 *
 * The two players' OWN groups, not every group in the round — which is the rule
 * the rest of the app's money already follows, and the comment at
 * schema.sql:255-270 records what produced it: a settlement computed from
 * whatever had been entered paid out the championship purse with the field
 * through twelve.
 *
 * A group that cannot be found counts as unsigned. A man who is in no foursome
 * for a round the match covers has no card to vouch for his scores, so there is
 * nothing to settle from.
 */
export function unsignedFor(
  doc: Pick<Tournament, 'groups'>,
  match: Pick<SideMatch, 'roundIds' | 'playerA' | 'playerB'>,
  confirmations: { roundId: string; groupId: string }[],
): { roundId: string; groupId: string }[] {
  const out: { roundId: string; groupId: string }[] = []
  const seen = new Set<string>()
  for (const roundId of match.roundIds) {
    for (const playerId of [match.playerA, match.playerB]) {
      const group: Group | undefined = doc.groups.find(
        (g) => g.roundId === roundId && g.playerIds.includes(playerId),
      )
      // Both men in the same foursome is one card, not two.
      const key = `${roundId}~${group?.id ?? `no-group:${playerId}`}`
      if (seen.has(key)) continue
      seen.add(key)
      const signed =
        group !== undefined &&
        confirmations.some((c) => c.roundId === roundId && c.groupId === group.id)
      if (!signed) out.push({ roundId, groupId: group?.id ?? '' })
    }
  }
  return out
}

export interface SideSettlement {
  match: SideMatch
  /** Cents. Positive means A collects from B. A voided match is always zero. */
  netCents: number
  /** Bets that cannot be called yet. Straight from the engine's ledger. */
  open: number
  /** Cards still to be signed. Empty means this figure is real money. */
  unsigned: { roundId: string; groupId: string }[]
  /** Nobody should Venmo off this number yet. */
  provisional: boolean
}

/**
 * What a match is worth, and whether anyone may believe it.
 *
 * A void settles at nothing, whatever the cards say — that is the point of
 * being able to void one. Everything else is provisional until both groups have
 * signed AND every bet has an answer, and a provisional figure is never
 * presented as a settled number.
 */
export function settle(
  doc: Pick<Tournament, 'groups'>,
  match: SideMatch,
  walk: NassauWalk,
  confirmations: { roundId: string; groupId: string }[],
): SideSettlement {
  if (match.voidedAt) {
    return { match, netCents: 0, open: 0, unsigned: [], provisional: false }
  }
  const unsigned = unsignedFor(doc, match, confirmations)
  return {
    match,
    // `ledger.net` is in the dollars the engine was handed, so it comes back
    // through the same boundary it went out through rather than being rounded
    // into a new one.
    netCents: Math.round(walk.ledger.net * 100),
    open: walk.ledger.open,
    unsigned,
    provisional: unsigned.length > 0 || walk.ledger.open > 0,
  }
}

export interface SidePairNet {
  /** The other man. */
  playerId: string
  /** Cents. Positive means the viewer collects. */
  cents: number
  /** How many of the matches behind this figure are not settled yet. */
  provisional: number
  matches: number
}

/**
 * Side money, netted per pair, from one man's point of view.
 *
 * Netted rather than listed because that is how it gets paid — one transfer per
 * pair at the end of the week, not one per bet. Kept deliberately away from the
 * tournament ledger: none of this is in the pot, the standings, the payouts or
 * `scheduledPayout`, and it never will be.
 *
 * A void contributes a pair with nothing in it rather than disappearing, so
 * "you and Springer are square" is a statement about a match that existed.
 */
export function nettedSideMoney(viewerId: string, settlements: SideSettlement[]): SidePairNet[] {
  const by = new Map<string, SidePairNet>()
  for (const s of settlements) {
    const { playerA, playerB } = s.match
    if (playerA !== viewerId && playerB !== viewerId) continue
    const other = playerA === viewerId ? playerB : playerA
    const mine = playerA === viewerId ? s.netCents : -s.netCents
    const row = by.get(other) ?? { playerId: other, cents: 0, provisional: 0, matches: 0 }
    row.cents += mine
    row.matches += 1
    if (s.provisional) row.provisional += 1
    by.set(other, row)
  }
  return [...by.values()].sort((a, b) => Math.abs(b.cents) - Math.abs(a.cents))
}
